import React, { useState } from 'react'
import { Autocomplete, Box, ListSubheader, MenuItem, TextField, Typography, useMediaQuery } from '@mui/material'
import { DateRangePickerSeeker } from '@ui-seeker/core'

import { AppFilterPanel, AppOfficesBlock, filterPanelGridSx } from '@components'
import { dsColorNew } from '@palette'
import { RANGES, RANGE_GROUPS, resolveRange, MAX_PICKUP_DAYS } from '../@services/ranges'
import { fmtRange, fromIso, toIso, daysBetween } from '../@services/dates'
import { BAND_ONE_ROW_MIN, BAND_ONE_ROW_QUERY, bandLayout } from '../@services/bandLayout'


/**
 * The band of a BI dashboard (CU-869fmhmx8 D8): the toggles on top, then the fields that ask the server again.
 * With `fields` (`{ office, lor, cars, pickup, captured }`, E7) the fields follow `bandLayout`: one row from 1100 px
 * of window with proportional widths, 3 + 2 below, never a field alone. `children` keep the plain 4-column grid for a
 * dashboard that does not name its fields. The dashboards to come (price curve, office network) reuse it.
 */
export default function ViewBand({ toggles, togglesRight, fields = null, extraRow = null, rows = null, children }) {
	const oneRow = useMediaQuery(BAND_ONE_ROW_QUERY)
	if (rows) {
		// rows of fields, each row its own grid of equal columns (CU-869fmjzrh D1: "what" and "when")
		return <AppFilterPanel toggles={toggles} togglesRight={togglesRight}>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
				{rows.map((row, i) => <Box key={i} sx={{ display: 'grid', gridTemplateColumns: oneRow ? `repeat(${row.length}, minmax(0, 1fr))` : `repeat(${Math.min(row.length, 2)}, minmax(0, 1fr))`, gap: '12px', alignItems: 'start' }}>
					{row.map((field, k) => <Box key={k} sx={{ minWidth: 0 }}>{field}</Box>)}
				</Box>)}
			</Box>
		</AppFilterPanel>
	}
	if (!fields) {
		return <AppFilterPanel toggles={toggles} togglesRight={togglesRight}>
			<Box sx={filterPanelGridSx(4)}>{children}</Box>
		</AppFilterPanel>
	}

	const layout = bandLayout(oneRow ? BAND_ONE_ROW_MIN : BAND_ONE_ROW_MIN - 1, { extraRow })
	return <AppFilterPanel toggles={toggles} togglesRight={togglesRight}>
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
			{layout.rows.map((row, i) => <Box key={row.join('-')} sx={{ display: 'grid', gridTemplateColumns: layout.columns[i], gap: '12px', alignItems: 'start' }}>
				{row.map(key => <Box key={key} sx={{ minWidth: 0 }}>{fields[key]}</Box>)}
			</Box>)}
		</Box>
	</AppFilterPanel>
}

// "Compare with" and the other pill groups carry a small label above them, as in the mock
export function ViewToggle({ label, children }) {
	return <Box>
		<Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle, mb: 0.75 }}>{label}</Typography>
		{children}
	</Box>
}

// The line under a field; a long one is cut with "…" and says itself whole on hover (E7)
export function FieldNote({ children, tone = 'muted', title }) {
	return <Typography component="div" title={title ?? (typeof children === 'string' ? children : undefined)} sx={{
		fontSize: 12, color: tone === 'info' ? dsColorNew.noteInfo : dsColorNew.textMuted, mt: 0.75, ml: 1.5,
		overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
	}}>{children}</Typography>
}

export function OfficeField({ value, options, onChange }) {
	const selected = options.find(o => Number(o.value) === Number(value)) || null
	return <Autocomplete
		size="small"
		disableClearable
		options={options}
		value={selected}
		onChange={(_, option) => option && onChange(Number(option.value))}
		getOptionLabel={o => o?.label || ''}
		isOptionEqualToValue={(a, b) => Number(a.value) === Number(b.value)}
		renderOption={(props, o) => <li {...props} key={o.value}><AppOfficesBlock name={o.label} type={o.office_type} country={o.country}/></li>}
		renderInput={params => <TextField {...params} label="Office"/>}
	/>
}

export function LorField({ value, options, onChange }) {
	const values = options.length ? options : [{ value, label: value }]
	return <TextField select size="small" fullWidth label="Rental length" value={values.some(o => o.value === value) ? value : ''} onChange={e => onChange(Number(e.target.value))}>
		{values.map(o => <MenuItem key={o.value} value={o.value}>{o.value} {o.value === 1 ? 'day' : 'days'}</MenuItem>)}
	</TextField>
}

/**
 * "Pick-up dates": the relative ranges by group with their dates, and "Fixed dates…" with the classic modules'
 * date range picker. The resolved dates under the field; `hint` is the line of a range changed for the user.
 */
export function RangeField({ value, from, to, today, hint, onChange, open, onOpenChange }) {
	const [picking, setPicking] = useState(false)
	const resolved = resolveRange(value, today, { from: fromIso(from), to: fromIso(to) })

	const items = []
	RANGE_GROUPS.forEach(group => {
		items.push(<ListSubheader key={`g-${group}`} sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle, lineHeight: '32px' }}>{group}</ListSubheader>)
		Object.entries(RANGES).filter(([, r]) => r.group === group).forEach(([key, r]) => {
			const dates = key === 'fixed' ? (value === 'fixed' ? resolved : null) : resolveRange(key, today)
			items.push(<MenuItem key={key} value={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, fontSize: 14 }}>
				<span>{r.label}{key === 'fixed' ? '…' : ''}</span>
				{!!dates && <Typography component="small" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{fmtRange(dates.from, dates.to)}</Typography>}
			</MenuItem>)
		})
	})

	return <Box>
		<TextField
			select size="small" fullWidth label="Pick-up dates" value={value}
			SelectProps={{
				open: !!open, onOpen: () => onOpenChange?.(true), onClose: () => onOpenChange?.(false),
				renderValue: key => RANGES[key]?.label || '',
				MenuProps: { PaperProps: { sx: { minWidth: 380 } } },
			}}
			onChange={e => {
				const key = e.target.value
				if (key === 'fixed') { setPicking(true); return }
				onChange({ range: key, from: null, to: null })
			}}
		>
			{items}
		</TextField>
		{(picking || value === 'fixed') && <Box sx={{ mt: 1 }}>
			<DateRangePickerSeeker
				label="Fixed dates"
				value={resolved && value === 'fixed' ? [resolved.from, resolved.to] : null}
				onChange={dates => {
					const [a, z] = Array.isArray(dates) ? dates : []
					if (!(a instanceof Date) || !(z instanceof Date)) return
					// the server takes up to 180 days of pick-ups
					const end = daysBetween(a, z) + 1 > MAX_PICKUP_DAYS ? new Date(a.getFullYear(), a.getMonth(), a.getDate() + MAX_PICKUP_DAYS - 1) : z
					setPicking(false)
					onChange({ range: 'fixed', from: toIso(a), to: toIso(end) })
				}}
			/>
		</Box>}
		{!!resolved && <FieldNote>{fmtRange(resolved.from, resolved.to)}</FieldNote>}
		{!!hint && <FieldNote tone="info">{hint}</FieldNote>}
	</Box>
}

// "Prices captured: Up to yesterday" and its date
export function CapturedField({ until }) {
	return <Box>
		<TextField size="small" fullWidth label="Prices captured" value="Up to yesterday" InputProps={{ readOnly: true }} sx={{ '& .MuiOutlinedInput-root': { backgroundColor: `${dsColorNew.white} !important` } }}/>
		{!!until && <FieldNote>{until}</FieldNote>}
	</Box>
}
